import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Plus, Search, MoreHorizontal, Eye, EyeOff, Copy, Trash2,
  ExternalLink, BarChart3, Link2, Globe, User, Grid3X3, List, ArrowUpDown,
  Activity, MousePointerClick, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { EmptyState } from '@/components/data-display';
import { toast } from 'sonner';
import { bioLinksApi } from '@/api/bioLinks';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import TrackingFields, { emptyTracking, trackingPayload } from '@/components/TrackingFields';
import { useRegionRequired } from '@/lib/partner';

const PER_PAGE = 50;

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'views_desc', label: 'Most Views' },
  { value: 'views_asc', label: 'Fewest Views' },
  { value: 'clicks_desc', label: 'Most Clicks' },
  { value: 'clicks_asc', label: 'Fewest Clicks' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
];

const VIEW_MODES = [
  { value: 'table', icon: List },
  { value: 'grid', icon: Grid3X3 },
];

export default function BioLinks() {
  const regionRequired = useRegionRequired();
  const navigate = useNavigate();
  const { getPagesDomain } = useAppSettings();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, skip: 0, limit: PER_PAGE, has_more: false });
  const [page, setPage] = useState(1);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState('all');

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newTracking, setNewTracking] = useState(emptyTracking);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, activeTab]);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: String((page - 1) * PER_PAGE),
        limit: String(PER_PAGE),
      };
      if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();
      if (activeTab !== 'all') params.status = activeTab;

      const data = await bioLinksApi.list(params);
      if (data.success) {
        setPages(data.data || []);
        // The bio API nests pagination under meta.
        setPagination(data.meta?.pagination || data.pagination || { total: (data.data || []).length, skip: (page - 1) * PER_PAGE, limit: PER_PAGE, has_more: false });
      }
    } catch (error) {
      console.error('Error fetching bio pages:', error);
      toast.error('Failed to load bio pages');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearchQuery, activeTab]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // Search + status are handled server-side.
  // Sort stays client-side (bio backend doesn't accept sort params) and only affects the current page.
  const filteredPages = useMemo(() => {
    const result = [...pages];
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.profile?.display_name || '').localeCompare(b.profile?.display_name || '');
        case 'name_desc':
          return (b.profile?.display_name || '').localeCompare(a.profile?.display_name || '');
        case 'views_desc':
          return (b.total_views || 0) - (a.total_views || 0);
        case 'views_asc':
          return (a.total_views || 0) - (b.total_views || 0);
        case 'clicks_desc':
          return (b.total_clicks || 0) - (a.total_clicks || 0);
        case 'clicks_asc':
          return (a.total_clicks || 0) - (b.total_clicks || 0);
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        default:
          return 0;
      }
    });
    return result;
  }, [pages, sortBy]);

  // Stats come from the server: the list above is one page, so counting it
  // would cap every card at PER_PAGE.
  const [stats, setStats] = useState({ total: 0, publishedCount: 0, draftCount: 0, totalViews: 0, totalClicks: 0, totalLinks: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const data = await bioLinksApi.stats();
      if (data.success) {
        const d = data.data || {};
        setStats({
          total: d.total || 0,
          publishedCount: d.published || 0,
          draftCount: d.draft || 0,
          totalViews: d.total_views || 0,
          totalClicks: d.total_clicks || 0,
          totalLinks: d.total_links || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching bio page stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // After a change: reload the current page and the headline counts.
  const refresh = () => {
    fetchPages();
    fetchStats();
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Please enter a display name');
      return;
    }
    if (regionRequired && !newTracking.region_id) {
      toast.error('Please select a region before creating this bio page.');
      return;
    }
    setCreating(true);
    try {
      const data = await bioLinksApi.create({
        display_name: newName,
        slug: newSlug || undefined,
        bio: '',
        tracking: trackingPayload(newTracking),
      });
      if (data.success) {
        toast.success('Bio page created!');
        setCreateDialogOpen(false);
        setNewName('');
        setNewSlug('');
        setNewTracking(emptyTracking);
        navigate(`/bio/${data.data.id}`);
      } else {
        toast.error(data.detail || 'Failed to create');
      }
    } catch (error) {
      toast.error('Failed to create bio page');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      const data = await bioLinksApi.publish(id);
      if (data.success) {
        toast.success('Published!');
        refresh();
      }
    } catch (error) {
      toast.error('Failed to publish');
    }
  };

  const handleUnpublish = async (id) => {
    try {
      const data = await bioLinksApi.unpublish(id);
      if (data.success) {
        toast.success('Unpublished');
        refresh();
      }
    } catch (error) {
      toast.error('Failed to unpublish');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const data = await bioLinksApi.duplicate(id);
      if (data.success) {
        toast.success('Duplicated!');
        refresh();
      }
    } catch (error) {
      toast.error('Failed to duplicate');
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archive this bio page?')) return;
    try {
      const data = await bioLinksApi.archive(id);
      if (data.success) {
        toast.success('Archived');
        refresh();
      }
    } catch (error) {
      toast.error('Failed to archive');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { className: 'bg-amber-500/10 text-amber-600 border-amber-200' },
      published: { className: 'bg-green-500/10 text-green-600 border-green-200' },
      archived: { className: 'bg-gray-500/10 text-gray-600 border-gray-200' }
    };
    const { className } = config[status] || config.draft;
    return (
      <Badge variant="outline" className={className}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  return (
    <div className="space-y-6" data-testid="bio-links-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Bio Links</h1>
          <p className="text-muted-foreground">Create link-in-bio pages for social media profiles</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-bio-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Bio Page
        </Button>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Bio Page</DialogTitle>
            <DialogDescription>Create a new link-in-bio page</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Display Name</label>
              <Input
                placeholder="e.g. John Smith"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="mt-1"
                data-testid="new-bio-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Custom URL (optional)</label>
              <div className="flex items-center mt-1">
                <span className="text-sm text-muted-foreground mr-1">{getPagesDomain()}/b/</span>
                <Input
                  placeholder="john-smith"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Organization</label>
              <div className="mt-2">
                <TrackingFields value={newTracking} onChange={setNewTracking} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !newName.trim() || (regionRequired && !newTracking.region_id)}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Bio Pages</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Globe className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.publishedCount}</p>
                <p className="text-xs text-muted-foreground">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Eye className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalViews.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Views</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <MousePointerClick className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalClicks.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Clicks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Status */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            All ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="published" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Published ({stats.publishedCount})
          </TabsTrigger>
          <TabsTrigger value="draft" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Draft ({stats.draftCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, slug, bio..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]" data-testid="sort-select">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border rounded-md">
            {VIEW_MODES.map(mode => (
              <Button
                key={mode.value}
                variant={viewMode === mode.value ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode(mode.value)}
                className="rounded-none first:rounded-l-md last:rounded-r-md"
                data-testid={`view-${mode.value}`}
              >
                <mode.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Results count */}
      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          Found {pagination.total ?? filteredPages.length} result{(pagination.total ?? filteredPages.length) !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      {/* Grid or Table View */}
      {loading ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Profile</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Links</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Clicks</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-10 w-[200px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[40px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[50px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[50px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[60px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[30px]" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : filteredPages.length === 0 ? (
        <EmptyState
          icon={Link2}
          title={searchQuery || activeTab !== 'all' ? 'No matching bio pages' : 'No bio pages yet'}
          description={searchQuery || activeTab !== 'all'
            ? 'Try adjusting your search or filters.'
            : 'A bio page is a single hub of links — perfect for "link in bio" on Instagram, TikTok, or LinkedIn. Create one to give followers a curated list of where to go next.'}
          actionLabel={!searchQuery && activeTab === 'all' ? 'Create Bio Page' : undefined}
          onAction={!searchQuery && activeTab === 'all' ? () => setCreateDialogOpen(true) : undefined}
        />
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="bio-grid">
          {filteredPages.map((page) => (
            <Card key={page.id} className="hover:shadow-md transition-shadow" data-testid={`bio-card-${page.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <Link to={`/bio/${page.id}`} className="flex items-center gap-3 hover:opacity-80">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={page.profile?.avatar_url} />
                      <AvatarFallback className="text-sm">
                        {getInitials(page.profile?.display_name)}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/bio/${page.id}`}>
                          <User className="w-4 h-4 mr-2" />
                          Edit
                        </Link>
                      </DropdownMenuItem>
                      {page.status === 'published' && (
                        <DropdownMenuItem asChild>
                          <a href={`/b/${page.slug}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Live
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleDuplicate(page.id)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => handleArchive(page.id)}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <Link to={`/bio/${page.id}`} className="block">
                  <h3 className="font-medium truncate mb-1">{page.profile?.display_name}</h3>
                  <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate block mb-3">
                    /b/{page.slug}
                  </code>
                </Link>
                <div className="flex items-center justify-between">
                  {getStatusBadge(page.status)}
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {(page.total_views || 0).toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Link2 className="w-3 h-3" />
                      {page.links?.length || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* Table View */
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Profile</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Links</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Clicks</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPages.map((page) => (
                  <TableRow key={page.id} data-testid={`bio-row-${page.id}`}>
                    <TableCell>
                      <Link to={`/bio/${page.id}`} className="flex items-center gap-3 hover:opacity-80">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={page.profile?.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {getInitials(page.profile?.display_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{page.profile?.display_name}</p>
                          {page.profile?.bio && (
                            <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                              {page.profile.bio}
                            </p>
                          )}
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-muted px-2 py-0.5 rounded">/b/{page.slug}</code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(`${window.location.origin}/b/${page.slug}`)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Link2 className="w-3 h-3 text-muted-foreground" />
                        <span>{page.links?.length || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3 text-muted-foreground" />
                        <span>{(page.total_views || 0).toLocaleString()}</span>
                      </div>
                    </TableCell>
                    <TableCell>{(page.total_clicks || 0).toLocaleString()}</TableCell>
                    <TableCell>{getStatusBadge(page.status)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/bio/${page.id}`}>
                              <User className="w-4 h-4 mr-2" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          {page.status === 'published' && (
                            <DropdownMenuItem asChild>
                              <a href={`/b/${page.slug}`} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4 mr-2" />
                                View Live
                              </a>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => copyToClipboard(`${window.location.origin}/b/${page.slug}`)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy URL
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {page.status === 'draft' ? (
                            <DropdownMenuItem onClick={() => handlePublish(page.id)}>
                              <Globe className="w-4 h-4 mr-2" />
                              Publish
                            </DropdownMenuItem>
                          ) : page.status === 'published' && (
                            <DropdownMenuItem onClick={() => handleUnpublish(page.id)}>
                              <EyeOff className="w-4 h-4 mr-2" />
                              Unpublish
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDuplicate(page.id)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleArchive(page.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {(pagination.total > 0 || pages.length > 0) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="pagination-summary">
            Page {page} of {Math.max(1, Math.ceil((pagination.total || 0) / PER_PAGE))} — {pagination.total || pages.length} total
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  data-testid="pagination-prev"
                  aria-disabled={page <= 1}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) setPage(p => p - 1);
                  }}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive onClick={(e) => e.preventDefault()}>
                  {page}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  data-testid="pagination-next"
                  aria-disabled={!pagination.has_more}
                  onClick={(e) => {
                    e.preventDefault();
                    if (pagination.has_more) setPage(p => p + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
